// See: backend/docs/arvento-telemetry.md §6 + §11
// Backend canlı snapshot endpoint'ini periyodik poll eder.
// Cron 30 sn yazıyor; UI refresh interval = TELEMETRY_THRESHOLDS.LIVE_POSITION_REFRESH_SECONDS (15 sn).

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useHttpClient } from "shared/hooks/http-hook";
import { TELEMETRY_THRESHOLDS } from "@routebot/constants";
import { platesMatch } from "shared/util/normalize-plate";

const REFRESH_MS = (TELEMETRY_THRESHOLDS?.LIVE_POSITION_REFRESH_SECONDS || 15) * 1000;

export const useTelemetryLive = ({ enabled = true } = {}) => {
  const { sendRequest } = useHttpClient();
  const [snapshots, setSnapshots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchLive = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/telemetry/live`);
      if (!mountedRef.current) return;
      setSnapshots(Array.isArray(data?.snapshots) ? data.snapshots : []);
      setLastLoadedAt(new Date());
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err?.message || "Telemetri verisi alınamadı");
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [sendRequest]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) return undefined;

    fetchLive();
    timerRef.current = setInterval(fetchLive, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, fetchLive]);

  return {
    snapshots,
    isLoading,
    error,
    lastLoadedAt,
    refresh: fetchLive,
    refreshMs: REFRESH_MS,
  };
};

// Envanter listesi olmayan demo hesabı rotayı plakayla eşler. Snapshot okuması /telemetry/live.
export const useTelemetryByPlate = ({ plate, enabled = true } = {}) => {
  const live = useTelemetryLive({ enabled: Boolean(enabled && plate) });
  const snapshot = useMemo(() => {
    if (!plate || !live.snapshots?.length) return null;
    return live.snapshots.find((item) => platesMatch(item.plate, plate)) || null;
  }, [live.snapshots, plate]);
  return { ...live, snapshot };
};

export default useTelemetryLive;
